
import React, { useCallback, useMemo, useState } from 'react';
import {
  RefinementListProps,
  useCurrentRefinements,
  useRefinementList,
} from 'react-instantsearch-hooks-web';

import { FacetProps } from './types';
import useFacetValueUniverse from '../../hooks/useFacetValueUniverse';
import useRefinementHelpers from '../../hooks/useRefinementHelpers';

import {
  CAPACITY_SIZE,
  LITRES_PER_MINUTE,
  OUTLET_LENGTH,
  PAN_HEIGHT,
  PAN_PROJECTION,
  RAIL_LENGTH,
  SHOWER_HEAD_SIZE,
} from '@/helpers/constants/attributes';

import Checkbox from '@/components/atoms/checkbox';

const BUCKETS = {
  [PAN_HEIGHT]: [
    { label: '<395 mm', min: -Infinity, max: 395 },
    { label: '396–419 mm', min: 396, max: 419 },
    { label: '420–439 mm', min: 420, max: 439 },
    { label: '>440 mm', min: 440, max: Infinity },
  ],
  [PAN_PROJECTION]: [
    { label: '<550 mm', min: -Infinity, max: 550 },
    { label: '551–650 mm', min: 551, max: 650 },
    { label: '651–700 mm', min: 651, max: 700 },
    { label: '>700 mm', min: 701, max: Infinity },
  ],
};

const INVALID_VALUES = [
  'na',
  'n/a',
  'not applicable',
  '0',
  '0 mm',
  '0mm',
  '0 l',
  '0l',
];

const sortByName: RefinementListProps['sortBy'] = (a, b) => {
  return a.name.localeCompare(b.name, undefined, {
    numeric: true,
  });
};

const getNumericValue = (value: string) => {
  const match = value.match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : NaN;
};

const TermFacet: React.FC<FacetProps> = ({ attribute }) => {
  const {
    refine,
    items: unsortedItems,
  } = useRefinementList({
    attribute,
    limit: 999,
  });

  /*
   * The refinement list only exposes values Algolia still returns. The
   * current refinements come straight from the search state, so they are the
   * authoritative list of what is actually applied for this attribute.
   */
  const { items: currentRefinementItems } = useCurrentRefinements({
    includedAttributes: [attribute],
  });

  const { resolveLabel } = useRefinementHelpers();

  const attributeName = attribute.split('.').pop() || '';

  const isCategory = attribute
    .toLowerCase()
    .includes('category');

  const isInvalid = (val: string) =>
    INVALID_VALUES.includes(
      val?.trim().toLowerCase(),
    );

  const items = unsortedItems
    .filter((item) => {
      const label =
        resolveLabel(attribute, item.label)
          ?.split('>')
          ?.pop() || item.label;

      return (
        item.count > 0 &&
        !isInvalid(label)
      );
    })
    .sort((a, b) => {
      if (isCategory) {
        const aLabel =
          resolveLabel(attribute, a.label)
            ?.split('>')
            ?.pop() || a.label;

        const bLabel =
          resolveLabel(attribute, b.label)
            ?.split('>')
            ?.pop() || b.label;

        return aLabel.localeCompare(
          bLabel,
          undefined,
          { numeric: true },
        );
      }

      return b.count - a.count;
    });

  const [showMore, setShowMore] = useState(false);

  const buckets = BUCKETS[
    attributeName as keyof typeof BUCKETS
  ];

  const isBucketFacet = !!buckets;

  /*
   * Complete value list for this attribute, scoped only to the page's base
   * filters, so applying a bucket covers its whole range rather than just the
   * values the other active filters happen to leave visible.
   */
  const universeValues = useFacetValueUniverse(attribute, isBucketFacet);

  const refinedValues = useMemo(
    () =>
      currentRefinementItems
        .flatMap((item) => item.refinements)
        .filter((refinement) => refinement.type !== 'numeric')
        .map((refinement) => String(refinement.label ?? refinement.value)),
    [currentRefinementItems],
  );

  const bucketItems = useMemo(() => {
    if (!buckets) {
      return [];
    }

    return buckets.map((bucket) => {
      const values = unsortedItems.filter((item) => {
        const label =
          resolveLabel(attribute, item.label)
            ?.split('>')
            ?.pop() || item.label;

        if (isInvalid(label)) {
          return false;
        }

        if (item.count <= 0 && !item.isRefined) {
          return false;
        }

        const numericValue =
          getNumericValue(label);

        return (
          !Number.isNaN(numericValue) &&
          numericValue >= bucket.min &&
          numericValue <= bucket.max
        );
      });

      /*
       * Every applied value that falls inside this bucket, even if it dropped
       * out of the facet response because of the other active filters.
       * Unchecking has to clear all of them or the bucket stays half applied.
       */
      const appliedValues = refinedValues.filter((value) => {
        const numericValue = getNumericValue(value);

        return (
          !Number.isNaN(numericValue) &&
          numericValue >= bucket.min &&
          numericValue <= bucket.max
        );
      });

      /*
       * Everything this bucket stands for. Falls back to the visible values
       * until the attribute's full value list has loaded.
       */
      const inRangeValues = universeValues.filter((value) => {
        if (isInvalid(value)) {
          return false;
        }

        const numericValue = getNumericValue(value);

        return (
          !Number.isNaN(numericValue) &&
          numericValue >= bucket.min &&
          numericValue <= bucket.max
        );
      });

      return {
        ...bucket,
        values,
        appliedValues,
        allValues: inRangeValues.length
          ? inRangeValues
          : values.map((value) => value.value),
        count: values.reduce(
          (total, value) =>
            total + value.count,
          0,
        ),
        isRefined: appliedValues.length > 0,
      };
    });
  }, [
    attribute,
    buckets,
    unsortedItems,
    refinedValues,
    universeValues,
    resolveLabel,
  ]);

  const toggleBucket = useCallback(
    (bucket: { appliedValues: string[]; allValues: string[] }) => {
      // Unchecking: clear every value the bucket actually has applied.
      if (bucket.appliedValues.length > 0) {
        bucket.appliedValues.forEach((value) => refine(value));
        return;
      }

      // Checking: nothing in range is refined yet, so apply the whole range.
      bucket.allValues.forEach((value) => refine(value));
    },
    [refine],
  );

  const visibleItemsCount = showMore
    ? items.length
    : 6;

  return (
    <div>
      <div className="flex flex-col gap-[18px] px-[2px] py-[16px] pl-[15px] lg:min-w-[300px]">
        {isBucketFacet
          ? bucketItems
              .filter((bucket) => bucket.count > 0 || bucket.isRefined)
              .map((bucket) => (
              <div
                key={bucket.label}
                className="flex items-center justify-start gap-[12px]"
              >
                <Checkbox
                  containerClassName="!gap-[0px]"
                  checked={bucket.isRefined}
                  className="!border-1 !border-[#090909] hover:cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();

                    toggleBucket(bucket);
                  }}
                />

                <div className="w-fit text-[16px] font-normal text-[#090909] md:text-[14px] lg:w-[12rem] 2xl:text-[16px]">
                  <p
                    className="w-fit hover:cursor-pointer"
                    onClick={() => toggleBucket(bucket)}
                  >
                    {bucket.label} ({bucket.count})
                  </p>
                </div>
              </div>
            ))
          : items
              .slice(0, visibleItemsCount)
              .map((term) => (
                <div
                  key={term.value}
                  className="flex items-center justify-start gap-[12px]"
                >
                  <Checkbox
                    containerClassName="!gap-[0px]"
                    checked={!!term.isRefined}
                    className="!border-1 !border-[#090909] hover:cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      refine(term.value);
                    }}
                  />

                  <div className="w-fit text-[16px] font-normal text-[#090909] md:text-[14px] lg:w-[12rem] 2xl:text-[16px]">
                    <p
                      className="w-fit hover:cursor-pointer"
                      onClick={() =>
                        refine(term.value)
                      }
                    >
                      {attributeName === PAN_HEIGHT ||
                      attributeName === PAN_PROJECTION ||
                      attributeName === RAIL_LENGTH ||
                      attributeName === SHOWER_HEAD_SIZE ||
                      attributeName === OUTLET_LENGTH
                        ? `${resolveLabel(attribute, term.label)?.split('>')?.pop()}mm`
                        : attributeName === LITRES_PER_MINUTE
                        ? `${resolveLabel(attribute, term.label)?.split('>')?.pop()}L/min`
                        : attributeName === CAPACITY_SIZE
                        ? `${resolveLabel(attribute, term.label)?.split('>')?.pop()}L`
                        : resolveLabel(attribute, term.label)?.split('>')?.pop() ===
                          'true'
                        ? 'Yes'
                        : resolveLabel(attribute, term.label)?.split('>')?.pop() ===
                          'false'
                        ? 'No'
                        : resolveLabel(attribute, term.label)?.split('>')?.pop()}{' '}
                      ({term.count})
                    </p>
                  </div>

                  <div className="flex items-center gap-[12px]" />
                </div>
              ))}
      </div>

      {!isBucketFacet && items.length > 6 && (
        <div className="px-[8px] md:!pb-[22px]">
          <button
            onClick={() =>
              setShowMore(!showMore)
            }
            className="text-[14px] text-[#666] underline hover:!cursor-pointer hover:text-[#090909]"
          >
            {showMore
              ? 'Show Less'
              : 'Show More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default TermFacet;

